import { TFunction } from 'i18next';

import { normalizeEditionPricing } from 'src/helpers/registrationPrice';
import { EditionPricingPeriod, IBaseEdition, IAgePriceMatrix } from 'src/interfaces/Edition';

export type EditionPriceTier = 'child' | 'young' | 'discounted' | 'full';

export type EditionPriceFields = Pick<
  IBaseEdition,
  'childPrice' | 'youngPrice' | 'discountedPrice' | 'fullPrice'
> &
  Partial<
    Pick<
      IBaseEdition,
      'pricing' | 'earlyBirdEndsAt' | 'signupDeadline' | 'childMaxAge' | 'groupPrice'
    >
  >;

export type EditionPriceMatch = {
  period: EditionPricingPeriod;
  tier: EditionPriceTier;
};

const PERIODS: EditionPricingPeriod[] = ['earlyBird', 'regular', 'atLocation'];

const TIER_FIELDS: { tier: EditionPriceTier; field: keyof IAgePriceMatrix }[] = [
  { tier: 'child', field: 'childPrice' },
  { tier: 'young', field: 'youngPrice' },
  { tier: 'discounted', field: 'discountedPrice' },
  { tier: 'full', field: 'fullPrice' },
];

/**
 * Which configured period×age-tier pairs match this amount.
 * When the same tier matches in all three periods (identical matrices), callers
 * can collapse to age-tier-only labels.
 */
export function getEditionPriceTiersForAmount(
  amount: number,
  edition: EditionPriceFields,
): EditionPriceMatch[] {
  const { pricing } = normalizeEditionPricing(edition);
  const matches: EditionPriceMatch[] = [];

  for (const period of PERIODS) {
    for (const { tier, field } of TIER_FIELDS) {
      if (pricing[period][field] === amount) {
        matches.push({ period, tier });
      }
    }
  }

  return matches;
}

/** True when every matching tier appears in all three periods (legacy flat / identical matrices). */
export function shouldCollapsePeriodLabels(matches: EditionPriceMatch[]): boolean {
  if (matches.length === 0) {
    return false;
  }
  const tiers = [...new Set(matches.map(m => m.tier))];
  return tiers.every(tier =>
    PERIODS.every(period => matches.some(m => m.period === period && m.tier === tier)),
  );
}

export function translatePriceTier(
  t: TFunction,
  tier: EditionPriceTier,
  childMaxAge = 6,
): string {
  if (tier === 'child') {
    return t('editions.priceTier.child', { max: childMaxAge });
  }
  if (tier === 'young') {
    return t('editions.priceTier.young', { min: childMaxAge + 1 });
  }
  return t(`editions.priceTier.${tier}`);
}

/** Human-readable label for a stored check-in price against the edition's pricing engine. */
export function formatEditionPriceAmountLabel(
  amount: number,
  edition: EditionPriceFields,
  t: TFunction,
): string {
  const { childMaxAge, groupPrice } = normalizeEditionPricing(edition);
  const matches = getEditionPriceTiersForAmount(amount, edition);
  const labels: string[] = [];

  if (matches.length > 0) {
    if (shouldCollapsePeriodLabels(matches)) {
      const tiers = [...new Set(matches.map(m => m.tier))];
      labels.push(...tiers.map(tier => translatePriceTier(t, tier, childMaxAge)));
    } else {
      labels.push(
        ...matches.map(
          ({ period, tier }) =>
            `${t(`editions.pricingPeriods.${period}`)} · ${translatePriceTier(t, tier, childMaxAge)}`,
        ),
      );
    }
  }

  if (typeof groupPrice === 'number' && !Number.isNaN(groupPrice) && groupPrice === amount) {
    labels.push(t('editions.priceTier.group'));
  }

  if (labels.length === 0) {
    return t('editions.priceTier.other');
  }
  return labels.join(' / ');
}
